import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import LgmButton from '../LgmButton';
import { useModalBackDismiss } from '../../navigation/useModalBackDismiss';
import { useApi } from '../../services/api/provider';
import { ProductService } from '../../services/api/domains/products/service';
import { ValidationJobsService } from '../../services/api/domains/validationJobs/service';
import { fetchAllProductIds } from '../../services/api/domains/products/fetchAllProductIds';
import { notifyValidationJobsPoll } from '../../shared/hooks/validationJobsPollBus';
import type { ProductIdsFilterParams } from '../../services/api/domains/products/fetchAllProductIds';
import type {
  ValidationJobSources,
  ValidationJobTargets,
} from '../../services/api/domains/validationJobs/types';

export interface ValidateProductsModalProps {
  open: boolean;
  onClose: () => void;
  /** Current grid filter (search/advancedSearch) — Run targets every matching product across all pages. */
  filterParams: ProductIdsFilterParams;
  /** Called with the new job_id once the trigger succeeds. */
  onTriggered?: (jobId: number) => void;
}

const SOURCE_OPTIONS: Array<{
  key: keyof ValidationJobSources;
  label: string;
}> = [
  { key: 'common_chemistry', label: 'Common Chemistry' },
  { key: 'pubchem', label: 'PubChem' },
  { key: 'fda_gsrs', label: 'FDA GSRS' },
];

const TARGET_OPTIONS: Array<{
  key: keyof ValidationJobTargets;
  label: string;
}> = [
  { key: 'ob', label: 'Orange Book (OB)' },
  { key: 'ndc', label: 'NDC' },
  { key: 'dmf', label: 'USDMF' },
];

/**
 * Validate Products devtool modal (issue #637, VPE-009 sub-task 9.3).
 *
 * Source/target checkboxes mirror the combined validate-products endpoint's
 * flags, all default on. Run pulls every product_id matching the grid's
 * current filter across all pages (Seq 10's fetchAllProductIds), then
 * triggers the endpoint and closes — it does not wait for the job itself to
 * finish (Architecture Decision 2's async execution model); progress is
 * tracked via the nav indicator / full jobs page (Seq 11/12).
 */
const ValidateProductsModal: React.FC<ValidateProductsModalProps> = ({
  open,
  onClose,
  filterParams,
  onTriggered,
}) => {
  const api = useApi();
  const productService = useMemo(() => new ProductService(api), [api]);
  const validationJobsService = useMemo(
    () => new ValidationJobsService(api),
    [api]
  );

  const [sources, setSources] = useState<Required<ValidationJobSources>>({
    common_chemistry: true,
    pubchem: true,
    fda_gsrs: true,
  });
  const [targets, setTargets] = useState<Required<ValidationJobTargets>>({
    ob: true,
    ndc: true,
    dmf: true,
  });
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A job with nothing enabled would have total_steps=0 and complete
  // immediately with no work done -- confusing "0/N products (0%)" history
  // clutter (Copilot review, PR #683; the server also rejects this with a
  // 400, but blocking Run here avoids the round trip and gives a clearer
  // local message).
  const nothingSelected =
    Object.values(sources).every(v => !v) &&
    Object.values(targets).every(v => !v);

  const handleClose = () => {
    if (isRunning) return;
    setError(null);
    onClose();
  };

  useModalBackDismiss({
    isOpen: open,
    onClose: handleClose,
    canDismiss: () => !isRunning,
  });

  const handleRun = async () => {
    setIsRunning(true);
    setError(null);
    try {
      const productIds = await fetchAllProductIds(productService, filterParams);
      if (productIds.length === 0) {
        // The server treats an empty product_ids array as "all products"
        // (api/routes/validateProducts.js), not "nothing matched" -- a zero-
        // result filter must not silently fall through to a full-dataset job.
        setError('No products match the current filter — nothing to validate.');
        return;
      }
      const response = await validationJobsService.trigger({
        product_ids: productIds,
        sources,
        targets,
      });
      notifyValidationJobsPoll();
      onTriggered?.(response.data.job_id);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to start the validation job. Please try again.'
      );
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Validate Products</DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={styles.description}>
          Runs CAS validation and Orange Book / NDC / USDMF linking for every
          product matching the current filter, across all pages. This runs as a
          background job — you can track its progress from the jobs page.
        </Typography>

        <Typography variant="subtitle2" style={styles.sectionLabel}>
          CAS Sources
        </Typography>
        <FormGroup row>
          {SOURCE_OPTIONS.map(({ key, label }) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={sources[key]}
                  disabled={isRunning}
                  onChange={e =>
                    setSources(prev => ({ ...prev, [key]: e.target.checked }))
                  }
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Typography variant="subtitle2" style={styles.sectionLabel}>
          Link Targets
        </Typography>
        <FormGroup row>
          {TARGET_OPTIONS.map(({ key, label }) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={targets[key]}
                  disabled={isRunning}
                  onChange={e =>
                    setTargets(prev => ({ ...prev, [key]: e.target.checked }))
                  }
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        {nothingSelected && (
          <Typography variant="body2" style={styles.errorText}>
            Select at least one CAS source or link target to run.
          </Typography>
        )}

        {error && (
          <Typography variant="body2" style={styles.errorText}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <LgmButton variant="flat" onClick={handleClose} disabled={isRunning}>
          Cancel
        </LgmButton>
        <LgmButton
          variant="contained"
          onClick={handleRun}
          disabled={isRunning || nothingSelected}>
          {isRunning ? 'Running…' : 'Run'}
        </LgmButton>
      </DialogActions>
    </Dialog>
  );
};

const styles: Record<string, React.CSSProperties> = {
  description: { marginBottom: '1rem', color: '#475569' },
  sectionLabel: { marginTop: '0.75rem', marginBottom: '0.25rem' },
  errorText: { marginTop: '0.75rem', color: '#c00' },
};

export default ValidateProductsModal;
